import isEqual from "lodash.isequal";

import { v4 } from "uuid";

import {
  ANCHOR_KEY_PATTERN,
  ComponentPlacementLocation,
  InsertType,
  OnsiteUpsellComponentSchema,
  SharedOnsiteUpsellComponent,
  SharedOnsiteUpsellComponentSchema,
} from "@intelligems/ig-types";
import { ONSITE_UPSELL_TEMPLATES } from "@intelligems/ig-utils";
import { isValidSelector } from "@src/store/slices/experienceBuilder/common/utils/html/isValidSelector";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import {
  ModHandler,
  OnsiteUpsellComponent,
  PlacementMode,
} from "@src/store/slices/experienceBuilder/types";
import {
  canonicalRulesToFlat,
  flatRulesToCanonical,
} from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsell/ruleMapping";

export const DEFAULT_PLACEMENT: Record<
  ComponentPlacementLocation,
  { selector: string; insertType: InsertType }
> = {
  pdp: { selector: 'form[action*="/cart/add"]', insertType: "afterend" },
  cart: { selector: ".cart__footer", insertType: "beforebegin" },
};

/**
 * Placement is one of two modes and the panel toggles between them. Switching
 * must CLEAR the other mode's fields: leaving a stale selector behind would
 * fail the schema's exactly-one-mode check with an error pointing at a field
 * the merchant can no longer see.
 */
export const DEFAULT_ANCHOR_KEY = "product-recommendations";

/** Which of the two placement modes a mod is currently in. */
export function placementMode(mod: { anchorKey?: string }): PlacementMode {
  return mod.anchorKey ? "themeBlock" : "selector";
}

export function placementForMode(
  mode: PlacementMode,
  location: ComponentPlacementLocation,
  previous?: { anchorKey?: string; selector?: string; insertType?: InsertType }
): Pick<OnsiteUpsellComponent, "anchorKey" | "selector" | "insertType"> {
  if (mode === "themeBlock") {
    return {
      anchorKey: previous?.anchorKey || DEFAULT_ANCHOR_KEY,
      selector: undefined,
      insertType: undefined,
    };
  }
  return {
    anchorKey: undefined,
    selector: previous?.selector || DEFAULT_PLACEMENT[location].selector,
    insertType: previous?.insertType ?? DEFAULT_PLACEMENT[location].insertType,
  };
}

// The fields that make two upsell mods meaningfully different (ignores meta).
const meaningful = (mod: OnsiteUpsellComponent) => ({
  location: mod.location,
  anchorKey: mod.anchorKey,
  selector: mod.selector,
  insertType: mod.insertType,
  maxProducts: mod.maxProducts,
  excludeOutOfStock: mod.excludeOutOfStock,
  excludeItemsInCart: mod.excludeItemsInCart,
  discount: mod.discount,
  rules: mod.rules,
  template: mod.template,
  description: mod.description,
  // The binding is part of what the merchant edits, so a rule change alone
  // has to enable Apply. The placement id is NOT here: it is stamped by the
  // server, so a re-read of the same component would otherwise read as an
  // unsaved change.
  agenticLogicRuleId: mod.agenticLogicRuleId,
});

export const onsiteUpsellModHandler: ModHandler<OnsiteUpsellComponent> = {
  normalize: (mod) => ({
    ...mod,
    anchorKey: mod.anchorKey?.trim() || undefined,
    selector: mod.selector?.trim(),
  }),

  // Emit the source canonical rules verbatim when the flat rules are unchanged,
  // preserving ids/priority and shapes the flat form can't express.
  convertFrom: (mod) => ({
    type: "component",
    dto: {
      id: mod.id,
      variationId: mod.variationId,
      config: {
        type: "onsiteUpsell",
        // Exactly one mode reaches the DTO. Emitting the unused mode's keys as
        // `undefined` would inflate the key count and read as unsaved changes
        // under lodash `isEqual` against the persisted config.
        placement: {
          ...(mod.anchorKey
            ? { anchorKey: mod.anchorKey }
            : { selector: mod.selector, insertType: mod.insertType }),
          location: mod.location,
        },
        template: mod.template,
        maxProductLimit: mod.maxProducts,
        excludeOutOfStock: mod.excludeOutOfStock,
        excludeItemsInCart: mod.excludeItemsInCart,
        // Match the persisted DTO, which omits `discount` when absent
        // (`UpsellDiscountSchema.optional()`); writing `undefined` would inflate
        // the key count and read as unsaved changes under lodash `isEqual`.
        ...(mod.discount ? { discount: mod.discount } : {}),
        rules:
          mod.canonicalRules &&
          isEqual(canonicalRulesToFlat(mod.canonicalRules), mod.rules)
            ? mod.canonicalRules
            : flatRulesToCanonical(mod.rules, mod.location, mod.canonicalRules),
        // Carried through verbatim. Dropping the placement id here would
        // DETACH an engine-backed component on every save from this panel;
        // dropping the rule id would make the save silent about rules, which
        // the backend reads as "preserve", not "detach".
        ...(mod.agenticLogicPlacementId === undefined
          ? {}
          : { agenticLogicPlacementId: mod.agenticLogicPlacementId }),
        ...(mod.agenticLogicRuleId === undefined
          ? {}
          : { agenticLogicRuleId: mod.agenticLogicRuleId }),
      },
    },
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "component") {
      throw new Error("Expected component conversion");
    }
    const { id, variationId, config } = conversion.dto;
    if (config.type !== "onsiteUpsell") {
      throw new Error(`Expected onsiteUpsell config, got ${config.type}`);
    }
    return {
      type: "onsiteUpsell",
      category: "component",
      id,
      variationId,
      location: config.placement.location,
      anchorKey: config.placement.anchorKey,
      selector: config.placement.selector,
      insertType: config.placement.insertType,
      maxProducts: config.maxProductLimit,
      excludeOutOfStock: config.excludeOutOfStock,
      excludeItemsInCart: config.excludeItemsInCart,
      discount: config.discount ?? null,
      rules: canonicalRulesToFlat(config.rules),
      template: config.template,
      canonicalRules: config.rules,
      agenticLogicPlacementId: config.agenticLogicPlacementId ?? null,
      agenticLogicRuleId: config.agenticLogicRuleId ?? null,
    };
  },

  // Reuse the shared schema (shape + domain + discount refinements) as the FE
  // validation layer; add selector validity, which the schema can't express.
  getErrors: (mod) => {
    const errors: Record<string, string> = {};
    const result = OnsiteUpsellComponentSchema.safeParse(
      onsiteUpsellModHandler.toAiFormat(mod)
    );
    if (!result.success) {
      for (const issue of result.error.issues) {
        errors[issue.path.join(".") || "form"] = issue.message;
      }
    }
    if (mod.selector && !isValidSelector(mod.selector)) {
      errors.selector = "Selector is invalid";
    }
    if (mod.anchorKey && !ANCHOR_KEY_PATTERN.test(mod.anchorKey)) {
      errors.anchorKey =
        "Use lowercase letters, numbers, dashes and underscores.";
    }
    return errors;
  },

  hasChanges: (oldMod, newMod) =>
    !isEqual(meaningful(oldMod), meaningful(newMod)),

  isEquivalent: (a, b) =>
    b.type === "onsiteUpsell" &&
    a.location === b.location &&
    a.anchorKey === b.anchorKey &&
    a.selector === b.selector,

  defaults: () => {
    const rules: OnsiteUpsellComponent["rules"] = [
      { conditions: [], products: { source: "recommendations" } },
    ];
    return {
      id: v4(),
      type: "onsiteUpsell",
      category: "component",
      variationId: "",
      location: "pdp",
      selector: DEFAULT_PLACEMENT.pdp.selector,
      insertType: DEFAULT_PLACEMENT.pdp.insertType,
      maxProducts: 3,
      excludeOutOfStock: true,
      excludeItemsInCart: true,
      discount: null,
      rules,
      canonicalRules: flatRulesToCanonical(rules, "pdp"),
      template: ONSITE_UPSELL_TEMPLATES.list,
    };
  },

  toAiFormat: (mod) =>
    pickSharedFields<SharedOnsiteUpsellComponent>(
      SharedOnsiteUpsellComponentSchema,
      mod
    ),

  fromAiFormat: (aiMod, context) => {
    const merged = {
      ...(context.existing ?? onsiteUpsellModHandler.defaults()),
      ...aiMod,
      ...sharedFromAiFormat(aiMod, context),
      category: "component" as const,
    };
    // A partial update can name one mode while the existing mod is in the
    // other, and the merge would carry both forward — an invalid mod. The
    // incoming mod decides, so drop whatever it did not name.
    const base = {
      ...merged,
      ...placementForMode(placementMode(aiMod), merged.location, aiMod),
    };
    return {
      ...base,
      canonicalRules: flatRulesToCanonical(
        base.rules,
        base.location,
        context.existing?.canonicalRules
      ),
    };
  },
};
